import { Button } from "@/components/core/buttons/button.tsx";
import type { ConfirmModalProps, DefaultModalProps, ModalAction, ModalProps, ModalState } from "@/components/core/modals/types.ts";
import * as ModalPrimitive from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { IconAlertTriangle, IconCheckbox, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes, type ReactNode, forwardRef, useReducer, useState } from "react";
import { ModalControlContext } from "./use-modal-controller.ts";
import { ModalContext } from "./use-modal.ts";

const Modal = ModalPrimitive.Root;
const ModalPortal = ModalPrimitive.Portal;

/* modal overlay */
/* modal background */
const ModalOverlay = forwardRef<ElementRef<typeof ModalPrimitive.Overlay>, ComponentPropsWithoutRef<typeof ModalPrimitive.Overlay>>(
	({ className, ...props }, ref) => (
		<ModalPrimitive.Overlay
			ref={ref}
			className={clsx(
				"fixed inset-0 z-[90] w-full h-full bg-slate-50/80 backdrop-blur-none",
				"data-[state=open]:animate-in data-[state=open]:fade-in-0",
				"data-[state=closed]:animate-out data-[state=closed]:fade-out-0 ",
				className,
			)}
			{...props}
		/>
	),
);
ModalOverlay.displayName = ModalPrimitive.Overlay.displayName;

/* modal content */
/* main dialog box */
const ModalContent = forwardRef<ElementRef<typeof ModalPrimitive.Content>, ComponentPropsWithoutRef<typeof ModalPrimitive.Content>>(
	({ className, children, ...props }, ref) => (
		<ModalPortal>
			<ModalOverlay className="flex justify-center overflow-y-auto">
				<ModalPrimitive.Content
					ref={ref}
					tabIndex={undefined}
					className={clsx(
						"relative flex flex-col w-full rounded-xl bg-white border border-border/50 shadow-xl shadow-slate-200 duration-200",
						"data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
						"data-[state=closed]:animate-out data-[state=closed]:fade-out-0  data-[state=closed]:zoom-out-95",
						className,
					)}
					{...props}
				>
					<VisuallyHidden>
						<ModalPrimitive.Title />
						<ModalPrimitive.Description />
					</VisuallyHidden>
					{children}
				</ModalPrimitive.Content>
			</ModalOverlay>
		</ModalPortal>
	),
);
ModalContent.displayName = ModalPrimitive.Content.displayName;

/* modal header */
const ModalHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div className={clsx("flex flex-row justify-center w-full rounded-t-xl p-4", className)} {...props} />
);
ModalHeader.displayName = "ModalHeader";

/* modal title */
const ModalTitle = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div className={clsx("text-xl font-semibold text-foreground-dark", className)} {...props} />
);
ModalTitle.displayName = "ModalTitle";

/* modal description */
const ModalDescription = forwardRef<ElementRef<typeof ModalPrimitive.Description>, ComponentPropsWithoutRef<typeof ModalPrimitive.Description>>(
	({ className, ...props }, ref) => <ModalPrimitive.Description ref={ref} className={clsx("text-base text-foreground/70", className)} {...props} />,
);
ModalDescription.displayName = ModalPrimitive.Description.displayName;

/* modal close button */
const ModalClose = ({ className, ...props }: HTMLAttributes<HTMLButtonElement>) => (
	<ModalPrimitive.Close
		className={clsx(
			"absolute top-2 right-2 flex w-[20px] h-[20px] ring-offset-background transition-opacity outline-none border-none ring-0 text-foreground opacity-40 hover:opacity-100",
			"disabled:pointer-events-none",
			className,
		)}
		{...props}
	>
		<IconX size={20} stroke={1.5} />
		<span className="sr-only">Close</span>
	</ModalPrimitive.Close>
);
ModalClose.displayName = "ModalClose";

/* modal icon */
/* used on confirm modal */
const ModalIcon = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
	<div className={clsx("inline-flex justify-center items-center shrink-0 grow-0 w-[44px] h-[44px] rounded-full", className)} {...props} />
);
ModalIcon.displayName = "ModalIcon";

/* modal body */
const ModalBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => <div className={clsx("flex flex-col w-full", className)} {...props} />;
ModalBody.displayName = "ModalBody";

/* modal footer */
const ModalFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={clsx("flex flex-row justify-between w-full rounded-b-xl p-4 gap-4", className)} {...props} />
);
ModalFooter.displayName = "ModalFooter";

/* base modal type */
const BaseModal = ({
	title,
	description,
	component,
	className,
	open,
	onOpenChange,
	size = "default",
	allowClose = true,
	showFooter = false,
	showHeader = true,
}: DefaultModalProps) => {
	const handleClose = () => {
		onOpenChange?.(false);
	};

	return (
		<Modal open={open} onOpenChange={handleClose}>
			<ModalContent
				className={clsx("", className, {
					"max-w-[420px] h-max max-h-max my-12": size === "sm",
					"max-w-[640px] h-max max-h-max my-12": size === "md" || size === "default",
					"max-w-[860px] h-max max-h-max my-12": size === "lg",
					"max-w-[1200px] h-max max-h-max my-12": size === "xl",
					"max-w-[1400px] h-max max-h-max my-12": size === "2xl",
					"max-w-full h-screen my-0 border-0": size === "full",
				})}
				onPointerDownOutside={(e) => e.preventDefault()}
			>
				{allowClose && <ModalClose />}
				{showHeader && (
					<ModalHeader className="pt-8 pb-0">
						<div className="flex flex-col justify-center items-center w-full">
							<ModalTitle className="text-center">{title}</ModalTitle>
							<ModalDescription className="text-center">{description}</ModalDescription>
						</div>
					</ModalHeader>
				)}
				<ModalBody className={clsx(size === "full" ? "min-h-screen p-0" : showHeader ? "p-6" : "p-6 pt-8")}>{component}</ModalBody>
				{showFooter && (
					<ModalFooter>
						<Button variant="ghost">Close</Button>
						<Button>Yes, Proceed</Button>
					</ModalFooter>
				)}
			</ModalContent>
		</Modal>
	);
};

/* confirm modal type */
const ConfirmModal = ({ title, description, open, onOpenChange, onConfirm, variant = "red" }: ConfirmModalProps) => {
	const [loading, setLoading] = useState(false);
	const handleClose = () => onOpenChange?.(false);

	const handleConfirm = async () => {
		setLoading(true);

		try {
			await onConfirm?.();

			handleClose();
		} catch (error) {
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal open={open} onOpenChange={handleClose}>
			<ModalContent className="self-center max-w-[380px] overflow-hidden" onPointerDownOutside={(e) => e.preventDefault()}>
				<ModalBody className="relative flex-col justify-center items-center p-6 gap-3 ">
					<div className={clsx("absolute top-0 left-0 right-0 w-full h-[4px]", variant === "red" && "bg-danger-600", variant === "green" && "bg-green-600")} />
					<ModalIcon
						className={clsx("self-center", variant === "red" && "bg-danger-100/60 text-danger-600", variant === "green" && "bg-green-100/60 text-green-600")}
					>
						{variant === "red" ? <IconAlertTriangle size={24} stroke={2} /> : <IconCheckbox size={24} stroke={2} />}
					</ModalIcon>
					<div className="flex flex-col justify-center items-center w-full gap-1">
						<ModalTitle className="!text-base text-center">{title}</ModalTitle>
						<ModalDescription className="!text-base text-center">{description}</ModalDescription>
					</div>
				</ModalBody>
				<ModalFooter className="!p-0 !gap-[1px]">
					<button
						onClick={() => {
							handleClose();
						}}
						type="button"
						className="flex items-center justify-center w-full px-3 h-[38px] bg-slate-50 hover:bg-slate-100 text-foreground  outline-none ring-0 border-0"
					>
						<span className="text-base font-semibold">Cancel</span>
					</button>
					<button
						onClick={handleConfirm}
						type="button"
						disabled={loading}
						className={clsx(
							"flex items-center justify-center w-full px-3 h-[38px] outline-none ring-0 border-0",
							"disabled:opacity-50 disabled:cursor-not-allowed",
							variant === "red" && "bg-danger-600 hover:bg-danger-500 text-danger-foreground",
							variant === "green" && "bg-green-600 hover:bg-green-500 text-white",
						)}
					>
						{loading ? (
							<span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
						) : (
							<span className="text-base font-semibold">Confirm</span>
						)}
					</button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

const actionTypes = {
	OPEN_MODAL: "OPEN_MODAL",
	CLOSE_MODAL: "CLOSE_MODAL",
	REMOVE_MODAL: "REMOVE_MODAL",
};

const reducer = (state: ModalState, action: ModalAction): ModalState => {
	switch (action.type) {
		case actionTypes.OPEN_MODAL:
			return {
				...state,
				modals: [...state.modals, { ...action.payload, isClosing: false }],
			};

		case actionTypes.CLOSE_MODAL:
			return {
				...state,
				modals: state.modals.map((modal) => (modal.id === action.payload.id ? { ...modal, isClosing: true } : modal)),
			};

		case actionTypes.REMOVE_MODAL:
			return {
				...state,
				modals: state.modals.filter((modal) => modal.id !== action.payload.id),
			};

		default:
			return state;
	}
};

const ModalProvider = ({ children }: { children?: ReactNode }) => {
	const [state, dispatch] = useReducer(reducer, { modals: [] });

	const openModal = (modalProps: ModalProps) => {
		const id = Date.now();

		if ("type" in modalProps && modalProps.type === "confirm") {
			// biome-ignore lint: <explanation>
			modalProps = { ...modalProps };
		} else {
			// biome-ignore lint: <explanation>
			modalProps = { ...modalProps, type: "default" };
		}

		dispatch({ type: "OPEN_MODAL", payload: { ...modalProps, id } });

		return id;
	};

	const getModal = (modalId: number) => state.modals.find((modal) => modal.id === modalId);

	const closeModal = (modalId: number) => {
		const currentModal = getModal(modalId);

		dispatch({ type: "CLOSE_MODAL", payload: { id: modalId } });

		if (currentModal !== undefined && currentModal.onDismiss !== undefined) {
			currentModal.onDismiss();
		}
	};

	const renderModals = () =>
		state.modals.map((modal) => (
			<ModalControlContext.Provider key={modal.id} value={{ closeModal: () => closeModal(modal.id) }}>
				<div key={modal.id}>
					{modal.type === "default" ? (
						<BaseModal {...modal} open={!modal.isClosing} onOpenChange={() => closeModal(modal.id)} />
					) : modal.type === "confirm" ? (
						<ConfirmModal {...modal} open={!modal.isClosing} onOpenChange={() => closeModal(modal.id)} />
					) : null}
				</div>
			</ModalControlContext.Provider>
		));

	return (
		<ModalContext.Provider value={{ state, openModal, closeModal }}>
			{renderModals()}
			{children}
		</ModalContext.Provider>
	);
};

export { ModalProvider, ModalContent, ModalHeader, ModalTitle, ModalOverlay };
